import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, MessageCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BRAND_OPTIONS = [
  "Mercedes-Benz",
  "BMW",
  "Audi",
  "Volvo",
  "Volkswagen",
  "Škoda",
  "Jeep",
  "Other",
];

const TIME_SLOTS = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
];

const INITIAL = {
  name: "",
  mobile: "",
  vehicle_brand: "",
  vehicle_model: "",
  registration_number: "",
  service_requirement: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm font-light text-ivory placeholder:text-ivory/30 outline-none transition-colors duration-300 focus:border-gold/60 [color-scheme:dark]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-ivory/50">
      {label}
    </span>
    {children}
  </label>
);

export default function Booking({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
    } else {
      window.__lenis?.start();
      setTimeout(() => {
        setSubmitted(false);
        setForm(INITIAL);
      }, 400);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, form);
    } catch (err) {
      console.error("Booking save failed", err);
    }
    const lines = [
      "Hi V-Elite Drives, I would like to book a service appointment.",
      "",
      `Name: ${form.name}`,
      `Mobile: ${form.mobile}`,
      `Vehicle: ${form.vehicle_brand} ${form.vehicle_model}`,
      form.registration_number && `Reg No: ${form.registration_number}`,
      `Service: ${form.service_requirement}`,
      (form.preferred_date || form.preferred_time) &&
        `Preferred: ${form.preferred_date} ${form.preferred_time}`,
      form.message && `Notes: ${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(
      `https://wa.me/919688996889?text=${encodeURIComponent(lines)}`,
      "_blank"
    );
    setSending(false);
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-ink/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            className="relative max-h-[92svh] w-full overflow-y-auto rounded-t-3xl border border-gold/20 bg-espresso p-6 sm:max-w-lg sm:rounded-3xl sm:p-8"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={onClose}
              data-testid="booking-close-button"
              aria-label="Close booking form"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-ivory/70 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
            >
              <X size={17} />
            </button>

            {!submitted ? (
              <>
                <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-gold">
                  V-Elite Drives · Tirupur
                </p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ivory">
                  Book a Service
                </h3>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <Field label="Name">
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      data-testid="booking-form-name-input"
                      className={inputCls}
                      placeholder="Your full name"
                    />
                  </Field>
                  <Field label="Mobile Number">
                    <input
                      required
                      type="tel"
                      pattern="[0-9+ ]{10,}"
                      value={form.mobile}
                      onChange={set("mobile")}
                      data-testid="booking-form-mobile-input"
                      className={inputCls}
                      placeholder="98765 43210"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Vehicle Brand">
                      <select
                        required
                        value={form.vehicle_brand}
                        onChange={set("vehicle_brand")}
                        data-testid="booking-form-brand-select"
                        className={inputCls}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        {BRAND_OPTIONS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Vehicle Model">
                      <input
                        required
                        value={form.vehicle_model}
                        onChange={set("vehicle_model")}
                        data-testid="booking-form-model-input"
                        className={inputCls}
                        placeholder="e.g. GLC 300"
                      />
                    </Field>
                  </div>
                  <Field label="Registration Number">
                    <input
                      value={form.registration_number}
                      onChange={set("registration_number")}
                      data-testid="booking-form-registration-input"
                      className={inputCls}
                      placeholder="TN 39 XX 0000"
                    />
                  </Field>
                  <Field label="Service Requirement">
                    <input
                      required
                      value={form.service_requirement}
                      onChange={set("service_requirement")}
                      data-testid="booking-form-service-input"
                      className={inputCls}
                      placeholder="e.g. Periodic service, brake check"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Preferred Date">
                      <input
                        type="date"
                        value={form.preferred_date}
                        onChange={set("preferred_date")}
                        data-testid="booking-form-date-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Preferred Time">
                      <select
                        value={form.preferred_time}
                        onChange={set("preferred_time")}
                        data-testid="booking-form-time-select"
                        className={inputCls}
                      >
                        <option value="">Select</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Message / Notes">
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={set("message")}
                      data-testid="booking-form-message-input"
                      className={`${inputCls} resize-none`}
                      placeholder="Anything we should know?"
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="booking-form-submit-button"
                    className="w-full rounded-full bg-gradient-to-br from-gold-light to-gold-deep py-4 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                  >
                    {sending ? "Sending..." : "Request Service"}
                  </button>
                  <p className="text-center text-[10px] uppercase tracking-[0.2em] text-ivory/35">
                    Your booking continues on WhatsApp
                  </p>
                </form>
              </>
            ) : (
              <div data-testid="booking-confirmation" className="py-10 text-center">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/40"
                >
                  <CheckCircle2 size={28} className="text-gold" />
                </motion.div>
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-ivory">
                  Thank You.
                </h3>
                <p className="mx-auto mt-3 max-w-xs text-sm font-light leading-relaxed text-ivory/60">
                  Our V-Elite Drives team will contact you shortly.
                </p>
                <div className="mt-8 flex flex-col gap-3">
                  <a
                    href="tel:+919688996889"
                    data-testid="booking-confirmation-call"
                    className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-gold-light to-gold-deep py-4 text-xs font-bold uppercase tracking-[0.22em] text-ink"
                  >
                    <Phone size={15} /> Call 96889 96889
                  </a>
                  <a
                    href="https://wa.me/919688996889?text=Hi%20V-Elite%20Drives%2C%20I%20would%20like%20to%20book%20a%20service%20appointment."
                    target="_blank"
                    rel="noreferrer"
                    data-testid="booking-confirmation-whatsapp"
                    className="flex items-center justify-center gap-2 rounded-full border border-gold/40 py-4 text-xs font-bold uppercase tracking-[0.22em] text-ivory"
                  >
                    <MessageCircle size={15} className="text-gold" /> WhatsApp Us
                  </a>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
